// navigation-completion Stage E — the target editor (local page / named
// destination / another document). Split from navShared.ts so that module
// exports no component.
//
// When `documentId` is provided, a third "Another document…" option mounts the
// RemoteTargetPicker (current/prior inventory, exact-bytes preview) which emits a
// server-authorized RemoteWireTarget. The option is always offered when a
// documentId is available — it is never hard-disabled; an unfinished remote
// selection is simply rejected (with a reason) at save time.

import type { RemoteNavigationPreviewCallback } from './useRemoteNavigationPreview';
import { type ReactElement } from 'react';
import { EMPTY_LOCAL_TARGET, EMPTY_REMOTE_TARGET, navStyles, type TargetDraft } from './navShared';
import { RemoteTargetPicker, type SavedRemoteSeed } from './RemoteTargetPicker';
import type { PdfPreviewFocus, PdfSelectionResult, SelectionKind } from './PdfCanvasViewer';

export interface TargetFieldsProps {
  readonly value: TargetDraft;
  readonly onChange: (next: TargetDraft) => void;
  readonly pageCount: number | null;
  readonly idPrefix: string;
  readonly disabled?: boolean;
  /** The SOURCE document whose link is being edited. When set, the "Another
   *  document…" (cross-document) option and its picker are offered. */
  readonly documentId?: string;
  /** Route a chosen cross-document target's exact bytes into a workspace viewer. */
  readonly onPreview?: RemoteNavigationPreviewCallback;
  /** Preview a LOCAL (in-document) page / named destination on the source
   *  document's own canvas BEFORE saving, so the reviewer can confirm the
   *  destination they typed resolves to the page they expect. */
  readonly onPreviewFocus?: (focus: PdfPreviewFocus) => void;
  /** Arm a visual destination selection on the source canvas. On completion the
   *  chosen page + landing point replace the local-page target. Offered only for
   *  the in-document ("Page in this document") target kind. */
  readonly onBeginSelect?: (kind: SelectionKind, onResult: (r: PdfSelectionResult) => void, opts?: { keepPreview?: boolean }) => void;
  /** A saved remote destination to re-select when retargeting a remote link, so
   *  "Change target" keeps the existing cross-document destination. */
  readonly savedRemote?: SavedRemoteSeed | null;
}

/** The current local/named draft as an in-document preview focus, or null when
 *  the input is not yet a previewable destination (empty name, non-finite page,
 *  or a cross-document selection the picker previews on its own). */
function localPreviewFocus(value: TargetDraft, pageCount: number | null): PdfPreviewFocus | null {
  if (value.kind === 'localPage') {
    const n = Number(value.pageInput);
    if (!Number.isInteger(n) || n < 1) return null;
    if (pageCount !== null && n > pageCount) return null;
    return { page: n, tone: 'destination', label: `Page ${n}` };
  }
  if (value.kind === 'named') {
    const name = value.name.trim();
    if (name.length === 0) return null;
    return { named: name, tone: 'destination', label: `“${name}”` };
  }
  return null;
}

export function TargetFields({ value, onChange, pageCount, idPrefix, disabled, documentId, onPreview, onPreviewFocus, onBeginSelect, savedRemote }: TargetFieldsProps): ReactElement {
  const onKindChange = (kind: string): void => {
    if (kind === 'named') onChange({ kind: 'named', name: '' });
    else if (kind === 'remote') onChange(EMPTY_REMOTE_TARGET);
    else onChange(EMPTY_LOCAL_TARGET);
  };

  const previewFocus = localPreviewFocus(value, pageCount);

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <select
        data-testid={`${idPrefix}-target-kind`}
        aria-label="Target kind"
        value={value.kind}
        disabled={disabled}
        style={navStyles.input}
        onChange={(e) => onKindChange(e.target.value)}
      >
        <option value="localPage">Page in this document</option>
        <option value="named">Named destination</option>
        {documentId && <option value="remote">Another document…</option>}
      </select>
      {value.kind === 'localPage' && (
        <input
          data-testid={`${idPrefix}-target-page`}
          aria-label="Target page"
          type="number"
          min={1}
          {...(pageCount !== null ? { max: pageCount } : {})}
          value={value.pageInput}
          disabled={disabled}
          style={{ ...navStyles.input, width: 72 }}
          onChange={(e) => {
            // Editing the page number by hand drops a previously-selected landing
            // point (it belonged to the old page) rather than silently keeping it.
            onChange({ kind: 'localPage', pageInput: e.target.value });
          }}
        />
      )}
      {value.kind === 'localPage' && onBeginSelect && (
        <button
          type="button"
          data-testid={`${idPrefix}-select-dest`}
          style={disabled ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn}
          disabled={disabled}
          onClick={() =>
            onBeginSelect('destination', (r) =>
              // The landing point is the TOP-LEFT of the selected area in PDF
              // user space (left = x0, top = y1), persisted as /XYZ left top null.
              onChange({ kind: 'localPage', pageInput: String(r.page), at: { x: r.rect.x0, y: r.rect.y1 } }),
            )
          }
        >
          Select on page…
        </button>
      )}
      {value.kind === 'localPage' && value.at && (
        <span style={navStyles.muted} data-testid={`${idPrefix}-dest-at`}>
          landing at ({value.at.x.toFixed(0)}, {value.at.y.toFixed(0)}){' '}
          <button
            type="button"
            data-testid={`${idPrefix}-dest-at-clear`}
            style={{ ...navStyles.smallBtn }}
            disabled={disabled}
            onClick={() => onChange({ kind: 'localPage', pageInput: value.pageInput })}
          >
            clear
          </button>
        </span>
      )}
      {value.kind === 'named' && (
        <input
          data-testid={`${idPrefix}-target-name`}
          aria-label="Named destination"
          type="text"
          value={value.name}
          disabled={disabled}
          style={{ ...navStyles.input, width: 160 }}
          onChange={(e) => onChange({ kind: 'named', name: e.target.value })}
        />
      )}
      {value.kind === 'remote' && documentId && (
        <RemoteTargetPicker
          documentId={documentId}
          value={value.selection}
          onChange={(selection) => onChange({ kind: 'remote', selection })}
          idPrefix={idPrefix}
          {...(disabled !== undefined ? { disabled } : {})}
          {...(onPreview ? { onPreview } : {})}
          {...(onBeginSelect ? { onBeginSelect } : {})}
          {...(savedRemote ? { savedRemote } : {})}
        />
      )}
      {onPreviewFocus && (value.kind === 'localPage' || value.kind === 'named') && (
        <button
          type="button"
          data-testid={`${idPrefix}-preview-dest`}
          style={!previewFocus || disabled ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn}
          disabled={!previewFocus || disabled}
          title={!previewFocus ? 'Enter a valid destination to preview it' : undefined}
          onClick={() => previewFocus && onPreviewFocus(previewFocus)}
        >
          Preview destination
        </button>
      )}
    </span>
  );
}
